import {
  type ColumnType,
  type Selectable,
  type Insertable,
  type Updateable,
} from 'kysely'
import { z } from 'zod'

import { projectId, type ProjectId } from './Project.js'
import {
  reportConfigurationId,
  type ReportConfigurationId,
} from './ReportConfiguration.js'
import { reportTemplateId, type ReportTemplateId } from './ReportTemplate.js'
import { userId, type UserId } from './User.js'

/** Identifier type for public.report_instance_ */
export type ReportInstanceId = string & { __brand: 'ReportInstanceId' }

/** Represents the table public.report_instance_ */
export default interface ReportInstanceTable {
  /** Database type: pg_catalog.uuid */
  id: ColumnType<
    ReportInstanceId,
    ReportInstanceId | undefined,
    ReportInstanceId
  >

  /** Database type: pg_catalog.timestamptz */
  createdAt: ColumnType<Date | null, Date | string | null, Date | string | null>

  /** Database type: pg_catalog.uuid */
  createdBy: ColumnType<UserId | null, UserId | null, UserId | null>

  /** Database type: pg_catalog.timestamptz */
  updatedAt: ColumnType<Date | null, Date | string | null, Date | string | null>

  /** Database type: pg_catalog.uuid */
  updatedBy: ColumnType<UserId | null, UserId | null, UserId | null>

  /** Database type: pg_catalog.timestamptz */
  deletedAt: ColumnType<Date | null, Date | string | null, Date | string | null>

  /** Database type: pg_catalog.uuid */
  deletedBy: ColumnType<UserId | null, UserId | null, UserId | null>

  /** Database type: pg_catalog.int4 */
  configurationId: ColumnType<
    ReportConfigurationId,
    ReportConfigurationId,
    ReportConfigurationId
  >

  /** Database type: pg_catalog.int4 */
  templateId: ColumnType<ReportTemplateId, ReportTemplateId, ReportTemplateId>

  /** Database type: pg_catalog.int4 */
  projectId: ColumnType<ProjectId, ProjectId, ProjectId>

  /** Database type: pg_catalog.date */
  reportPeriodStart: ColumnType<Date, Date | string, Date | string>

  /** Database type: pg_catalog.date */
  reportPeriodEnd: ColumnType<Date, Date | string, Date | string>

  /** Database type: pg_catalog.text */
  s3Url: ColumnType<string | null, string | null, string | null>

  /** Database type: pg_catalog.text */
  status: ColumnType<string, string | undefined, string>

  /** Database type: pg_catalog.text */
  errorMessage: ColumnType<string | null, string | null, string | null>
}

export type ReportInstance = Selectable<ReportInstanceTable>

export type ReportInstanceInitializer = Insertable<ReportInstanceTable>

export type ReportInstanceMutator = Updateable<ReportInstanceTable>

export const reportInstanceId =
  z.string() as unknown as z.Schema<ReportInstanceId>

export const reportInstance = z.object({
  id: reportInstanceId,
  createdAt: z.coerce.date().nullable().optional(),
  createdBy: userId.nullable().optional(),
  updatedAt: z.coerce.date().nullable().optional(),
  updatedBy: userId.nullable().optional(),
  deletedAt: z.coerce.date().nullable().optional(),
  deletedBy: userId.nullable().optional(),
  configurationId: reportConfigurationId,
  templateId: reportTemplateId,
  projectId: projectId,
  reportPeriodStart: z.coerce.date(),
  reportPeriodEnd: z.coerce.date(),
  s3Url: z.string().nullable().optional(),
  status: z.string(),
  errorMessage: z.string().nullable().optional(),
}) as unknown as z.Schema<ReportInstance>

export const reportInstanceInitializer = z.object({
  id: reportInstanceId.optional(),
  createdAt: z.coerce.date().optional().nullable(),
  createdBy: userId.optional().nullable(),
  updatedAt: z.coerce.date().optional().nullable(),
  updatedBy: userId.optional().nullable(),
  deletedAt: z.coerce.date().optional().nullable(),
  deletedBy: userId.optional().nullable(),
  configurationId: reportConfigurationId,
  templateId: reportTemplateId,
  projectId: projectId,
  reportPeriodStart: z.coerce.date(),
  reportPeriodEnd: z.coerce.date(),
  s3Url: z.string().optional().nullable(),
  status: z.string().optional(),
  errorMessage: z.string().optional().nullable(),
}) as unknown as z.Schema<ReportInstanceInitializer>

export const reportInstanceMutator = z.object({
  id: reportInstanceId.optional(),
  createdAt: z.coerce.date().optional().nullable(),
  createdBy: userId.optional().nullable(),
  updatedAt: z.coerce.date().optional().nullable(),
  updatedBy: userId.optional().nullable(),
  deletedAt: z.coerce.date().optional().nullable(),
  deletedBy: userId.optional().nullable(),
  configurationId: reportConfigurationId.optional(),
  templateId: reportTemplateId.optional(),
  projectId: projectId.optional(),
  reportPeriodStart: z.coerce.date().optional(),
  reportPeriodEnd: z.coerce.date().optional(),
  s3Url: z.string().optional().nullable(),
  status: z.string().optional(),
  errorMessage: z.string().optional().nullable(),
}) as unknown as z.Schema<ReportInstanceMutator>
